import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import HttpService from '../../services/httpService';
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Button, FormControl, InputLabel, Select, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, TextField,
    CircularProgress, Snackbar, SnackbarContent, LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import VisibilityIcon from '@material-ui/icons/Visibility';
import FacebookIcon from '@material-ui/icons/Facebook';
import InstagramIcon from '@material-ui/icons/Instagram';
import RestoreIcon from '@material-ui/icons/Restore';
import FilterListIcon from '@material-ui/icons/FilterList';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import ScheduleIcon from '@material-ui/icons/Schedule';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import * as XLSX from 'xlsx';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: '#f3f5f8',
        minHeight: '100vh',
        padding: '28px 32px 48px',
    },
    page: { maxWidth: 1920, width: '100%', margin: '0 auto' },
    title: { fontSize: 24, fontWeight: 700, color: '#1C375D', margin: 0, letterSpacing: 0.2 },
    subtitle: {
        fontSize: 13, color: '#8a92a3', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6,
    },
    headerCard: {
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 20, marginBottom: 20,
        backgroundColor: '#fff', borderRadius: 12, padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    headerActions: {
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    },
    filtersCard: {
        padding: '18px 24px', marginBottom: 20, borderRadius: 12,
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
    },
    filtersTitle: {
        display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700,
        color: '#1C375D', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 14,
    },
    filtersRow: {
        display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
    },
    formControl: { minWidth: 180 },
    statsBar: {
        display: 'flex', gap: 18, marginBottom: 22, flexWrap: 'wrap',
    },
    statCard: {
        display: 'flex', alignItems: 'center', gap: 12,
        backgroundColor: '#fff', borderRadius: 10, padding: '14px 22px',
        boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)', flex: '1 1 220px', minWidth: 220, maxWidth: 360,
    },
    statIcon: {
        width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center',
        justifyContent: 'center', backgroundColor: '#1C375D14', color: '#1C375D', flexShrink: 0,
    },
    statLabel: {
        fontSize: 11.5, color: '#8a92a3', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    },
    statValue: { fontSize: 18, fontWeight: 700, color: '#1C375D', marginTop: 2 },
    tableWrap: {
        borderRadius: 12, boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)', overflow: 'hidden',
    },
    tableRelative: { position: 'relative' },
    tableContainer: { maxHeight: '65vh' },
    tableLoadingOverlay: {
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 3,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
    },
    tableLoadingText: { fontSize: 13, fontWeight: 600, color: '#1C375D' },
    headCell: {
        padding: '15px 20px', fontWeight: 700, backgroundColor: '#1C375D', color: '#FFF2EB',
        whiteSpace: 'nowrap', fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase',
        borderBottom: 'none', position: 'sticky', top: 0, zIndex: 2,
    },
    bodyCell: { padding: '13px 20px', fontSize: 13.5, borderBottom: '1px solid #eef0f3' },
    tableRow: {
        transition: 'background-color 0.15s ease',
        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
        '&:hover': { backgroundColor: '#eef2f8' },
    },
    downloadBtn: {
        backgroundColor: '#355025', color: '#FFF2EB', textTransform: 'none', fontWeight: 600,
        borderRadius: 8, padding: '8px 18px', boxShadow: 'none',
        '&:hover': { backgroundColor: '#28401c', boxShadow: 'none' },
    },
    recoverBtn: {
        backgroundColor: '#1C375D', color: '#FFF2EB', textTransform: 'none', fontWeight: 600,
        borderRadius: 8, padding: '8px 18px', boxShadow: 'none',
        '&:hover': { backgroundColor: '#152a47', boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: '#9aa8bc', color: '#f5f5f5' },
    },
    applyBtn: {
        textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '8px 20px', boxShadow: 'none',
    },
    resetBtn: {
        textTransform: 'none', fontWeight: 600, borderRadius: 8, padding: '8px 20px',
    },
    detailGrid: {
        display: 'grid', gridTemplateColumns: '160px 1fr', gap: '10px 16px', fontSize: 14,
    },
    detailLabel: { fontWeight: 600, color: '#555' },
    customFields: {
        marginTop: 12, padding: 12, backgroundColor: '#f9f9f9', borderRadius: 8,
        fontSize: 13, whiteSpace: 'pre-wrap',
    },
}));

const TIME_OPTIONS = [
    { value: '', label: 'All time' },
    { value: '1', label: 'Last 1 hour' },
    { value: '6', label: 'Last 6 hours' },
    { value: '24', label: 'Last 24 hours' },
    { value: '48', label: 'Last 48 hours' },
    { value: '168', label: 'Last 7 days' },
    { value: '720', label: 'Last 30 days' },
];

const SOURCE_OPTIONS = [
    { value: '', label: 'All sources' },
    { value: 'Instagram', label: 'Instagram' },
    { value: 'Facebook', label: 'Facebook' },
];

const STATUS_OPTIONS = [
    { value: '', label: 'All statuses' },
    { value: 'NEW', label: 'NEW' },
];

function PlatformSourceBadge({ platform, showLabel = true }) {
    const normalized = String(platform || '').toLowerCase();

    if (normalized === 'instagram') {
        return (
            <Chip
                size="small"
                icon={<InstagramIcon style={{ color: '#E1306C', fontSize: 18 }} />}
                label={showLabel ? 'Instagram' : ''}
                style={{
                    backgroundColor: '#E1306C18',
                    color: '#C13584',
                    fontWeight: 600,
                    paddingLeft: showLabel ? undefined : 4,
                }}
            />
        );
    }

    if (normalized === 'facebook') {
        return (
            <Chip
                size="small"
                icon={<FacebookIcon style={{ color: '#1877F2', fontSize: 18 }} />}
                label={showLabel ? 'Facebook' : ''}
                style={{
                    backgroundColor: '#1877F218',
                    color: '#1877F2',
                    fontWeight: 600,
                    paddingLeft: showLabel ? undefined : 4,
                }}
            />
        );
    }

    return <Chip size="small" label={platform || 'Unknown'} />;
}

function SourceFilterOption({ value, label }) {
    if (value === 'Instagram') {
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <InstagramIcon style={{ color: '#E1306C', fontSize: 20 }} />
                {label}
            </span>
        );
    }
    if (value === 'Facebook') {
        return (
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FacebookIcon style={{ color: '#1877F2', fontSize: 20 }} />
                {label}
            </span>
        );
    }
    return label;
}

function formatDateTime(value) {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function leadDate(lead) {
    return lead.formFilledAt || lead.createdTime || lead.createdAt;
}

function flattenLeadForCsv(lead, index) {
    const custom = lead.customFields
        ? Object.entries(lead.customFields).map(([k, v]) => `${k}: ${v}`).join(' | ')
        : '';
    return {
        serialNo: index + 1,
        leadNo: lead.lead_no || '',
        fullName: lead.fullName || '',
        email: lead.email || '',
        phone: lead.phone || '',
        city: lead.city || '',
        state: lead.state || '',
        pincode: lead.pincode || lead.zip || '',
        platform: lead.platform || '',
        status: lead.status || '',
        formName: lead.formName || '',
        formId: lead.formId || '',
        pageName: lead.pageName || '',
        pageId: lead.pageId || '',
        campaignName: lead.campaignName || '',
        campaignId: lead.campaignId || '',
        adsetName: lead.adsetName || '',
        adsetId: lead.adsetId || '',
        adName: lead.adName || '',
        adId: lead.adId || '',
        leadgenId: lead.leadgenId || '',
        formFilledAt: formatDateTime(lead.formFilledAt),
        syncedAt: formatDateTime(lead.syncedAt),
        customFields: custom,
    };
}

function LeadDetailsDialog({ open, lead, onClose, classes }) {
    const [decryptedPhone, setDecryptedPhone] = useState(null);
    const [phoneLoading, setPhoneLoading] = useState(false);
    const [phoneError, setPhoneError] = useState('');

    useEffect(() => {
        if (!open || !lead?.leadgenId) {
            setDecryptedPhone(null);
            setPhoneError('');
            return undefined;
        }

        let cancelled = false;
        setPhoneLoading(true);
        setPhoneError('');
        setDecryptedPhone(null);

        HttpService.getMetaLeadPhone(lead.leadgenId)
            .then((response) => {
                if (cancelled) return;
                setDecryptedPhone(response.data?.phone || null);
            })
            .catch((err) => {
                if (cancelled) return;
                setPhoneError(err?.response?.data?.message || 'Unable to decrypt phone number');
            })
            .finally(() => {
                if (!cancelled) setPhoneLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, lead?.leadgenId]);

    if (!lead) return null;

    const displayPhone = decryptedPhone || (lead.phone && !/^X+\d{4}$/i.test(lead.phone) ? lead.phone : null);

    const rows = [
        ['Lead No.', lead.lead_no],
        ['Status', lead.status],
        ['Full Name', lead.fullName],
        ['Email', lead.email],
        ['Phone (masked in list)', lead.phone],
        ['City', lead.city],
        ['State', lead.state],
        ['Pincode', lead.pincode || lead.zip],
        ['Other Location', lead.otherLocation],
        ['Platform / Source', lead.platform],
        ['Page Name', lead.pageName],
        ['Form Name', lead.formName],
        ['Campaign', lead.campaignName],
        ['Ad Set', lead.adsetName],
        ['Ad Name', lead.adName],
        ['Leadgen ID', lead.leadgenId],
        ['Form ID', lead.formId],
        ['Page ID', lead.pageId],
        ['Ad ID', lead.adId],
        ['Campaign ID', lead.campaignId],
        ['Form Filled At', formatDateTime(lead.formFilledAt)],
        ['Created Time', formatDateTime(lead.createdTime)],
        ['Synced At', formatDateTime(lead.syncedAt)],
        ['Created At', formatDateTime(lead.createdAt)],
        ['Updated At', formatDateTime(lead.updatedAt)],
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Meta Lead — {lead.fullName || 'Details'}</DialogTitle>
            <DialogContent dividers>
                {/* <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#eef6ee', borderRadius: 8 }}>
                    <div className={classes.detailLabel} style={{ marginBottom: 6 }}>Decrypted Phone</div>
                    {phoneLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <CircularProgress size={18} />
                            <span>Decrypting phone...</span>
                        </div>
                    ) : phoneError ? (
                        <span style={{ color: '#c62828' }}>{phoneError}</span>
                    ) : (
                        <span style={{ fontSize: 16, fontWeight: 700, color: '#1C375D' }}>
                            {displayPhone || 'N/A'}
                        </span>
                    )}
                </div> */}
                <div className={classes.detailGrid}>
                    {rows.map(([label, value]) => (
                        <React.Fragment key={label}>
                            <div className={classes.detailLabel}>{label}</div>
                            <div>
                                {label === 'Platform / Source' ? (
                                    <PlatformSourceBadge platform={value} />
                                ) : label === 'Lead No.' && value && lead.erpLeadId ? (
                                    <Link
                                        to={`/project-logs/${lead.erpLeadId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: '#1C375D', fontWeight: 600, textDecoration: 'underline' }}
                                    >
                                        {value}
                                    </Link>
                                ) : (value ?? 'N/A')}
                            </div>
                        </React.Fragment>
                    ))}
                </div>
                {lead.customFields && Object.keys(lead.customFields).length > 0 && (
                    <>
                        <div style={{ marginTop: 16, fontWeight: 700 }}>Custom Form Fields</div>
                        <div className={classes.customFields}>
                            {Object.entries(lead.customFields).map(([key, val]) => (
                                <div key={key} style={{ marginBottom: 6 }}>
                                    <strong>{key.replace(/_/g, ' ')}:</strong> {String(val)}
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary">Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function MetaLeads() {
    const classes = useStyles();
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [loading, setLoading] = useState(false);
    const [since, setSince] = useState(null);

    const [hours, setHours] = useState('24');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [platform, setPlatform] = useState('');
    const [status, setStatus] = useState('');

    const [appliedFilters, setAppliedFilters] = useState({
        hours: '24', startDate: '', endDate: '', platform: '', status: '',
    });

    const [selectedLead, setSelectedLead] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [recovering, setRecovering] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

    const buildLeadQueryFilters = useCallback(() => {
        const hasDateRange = !!(appliedFilters.startDate || appliedFilters.endDate);
        const isAllTime = !hasDateRange && (appliedFilters.hours === '' || appliedFilters.hours == null);

        return {
            // Prefer explicit date range; otherwise use hours (or all-time)
            hours: hasDateRange || isAllTime ? undefined : appliedFilters.hours,
            all: isAllTime || undefined,
            startDate: appliedFilters.startDate || undefined,
            endDate: appliedFilters.endDate || undefined,
            platform: appliedFilters.platform || undefined,
            status: appliedFilters.status || undefined,
        };
    }, [appliedFilters]);

    const fetchLeads = useCallback(async () => {
        setLoading(true);
        try {
            const response = await HttpService.getMetaLeads({
                page: page + 1,
                limit: rowsPerPage,
                ...buildLeadQueryFilters(),
            });

            const data = response.data || {};
            const items = data.leads || [];

            setLeads(items);
            setTotal(Number(data.total) || 0);
            setSince(data.since || null);
        } catch (err) {
            console.error(err);
            setLeads([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, buildLeadQueryFilters]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const handleApplyFilters = () => {
        setPage(0);
        setAppliedFilters({ hours, startDate, endDate, platform, status });
    };

    const handleResetFilters = () => {
        setHours('24');
        setStartDate('');
        setEndDate('');
        setPlatform('');
        setStatus('');
        setPage(0);
        setAppliedFilters({ hours: '24', startDate: '', endDate: '', platform: '', status: '' });
    };

    const handleRecoverMissedLeads = async () => {
        const lookbackHours = Number(appliedFilters.hours) || Number(hours) || 24;
        setRecovering(true);
        try {
            const response = await HttpService.recoverMetaMissedLeads({
                hours: lookbackHours,
                limitPerForm: 500,
                retryLimit: 100,
            });
            const data = response?.data || {};
            const summary = data.summary || {};
            if (data.success === false) {
                setSnack({
                    open: true,
                    severity: 'error',
                    message: data.message || 'Recovery failed',
                });
                return;
            }
            setSnack({
                open: true,
                severity: 'success',
                message: `Recovery done (last ${lookbackHours}h): fetched ${summary.leadsFetchedFromMeta ?? 0} from Meta, ERP retry ok ${summary.retryOk ?? 0}, failed ${summary.retryFailed ?? 0}`,
            });
            await fetchLeads();
        } catch (err) {
            setSnack({
                open: true,
                severity: 'error',
                message: err?.response?.data?.message || err?.message || 'Recovery failed',
            });
        } finally {
            setRecovering(false);
        }
    };

    const excelHeaders = [
        { key: 'serialNo', label: 'Sl. No.' },
        { key: 'leadNo', label: 'Lead No.' },
        { key: 'fullName', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'city', label: 'City' },
        { key: 'state', label: 'State' },
        { key: 'pincode', label: 'Pincode' },
        { key: 'platform', label: 'Source / Platform' },
        { key: 'status', label: 'Status' },
        { key: 'formName', label: 'Form Name' },
        { key: 'formId', label: 'Form ID' },
        { key: 'pageName', label: 'Page Name' },
        { key: 'pageId', label: 'Page ID' },
        { key: 'campaignName', label: 'Campaign' },
        { key: 'campaignId', label: 'Campaign ID' },
        { key: 'adsetName', label: 'Ad Set' },
        { key: 'adsetId', label: 'Adset ID' },
        { key: 'adName', label: 'Ad Name' },
        { key: 'adId', label: 'Ad ID' },
        { key: 'leadgenId', label: 'Leadgen ID' },
        { key: 'formFilledAt', label: 'Form Filled At' },
        { key: 'syncedAt', label: 'Synced At' },
        { key: 'customFields', label: 'Custom Fields' },
    ];

    const DOWNLOAD_PAGE_SIZE = 1000;

    const fetchAllLeadsForDownload = useCallback(async () => {
        const filters = buildLeadQueryFilters();
        let allLeads = [];
        let downloadPage = 1;
        let totalCount = Infinity;

        while (allLeads.length < totalCount) {
            const response = await HttpService.getMetaLeads({
                page: downloadPage,
                limit: DOWNLOAD_PAGE_SIZE,
                ...filters,
            });
            const data = response.data || {};
            const items = data.leads || [];
            totalCount = Number(data.total) || 0;

            allLeads = allLeads.concat(items);
            if (!items.length) break;
            downloadPage += 1;
        }

        return allLeads;
    }, [buildLeadQueryFilters]);

    const handleDownloadExcel = async () => {
        if (!total) return;

        setDownloading(true);
        try {
            const allLeads = await fetchAllLeadsForDownload();

            const rows = allLeads.map((lead, index) => {
                const flat = flattenLeadForCsv(lead, index);
                const row = {};
                excelHeaders.forEach(({ key, label }) => {
                    row[label] = flat[key] ?? '';
                });
                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Meta Leads');
            XLSX.writeFile(workbook, `meta_leads_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (err) {
            console.error(err);
            setSnack({
                open: true,
                severity: 'error',
                message: err?.response?.data?.message || err?.message || 'Failed to download leads',
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className={classes.root}>
            <div className={classes.page}>
                <div className={classes.headerCard}>
                    <div>
                        <h1 className={classes.title}>Meta Leads</h1>
                        <div className={classes.subtitle}>
                            Facebook / Instagram lead ads — from Meta API
                            {loading ? ' · Loading...' : ''}
                        </div>
                    </div>
                    <div className={classes.headerActions}>
                        <Button
                            className={classes.recoverBtn}
                            variant="contained"
                            startIcon={recovering ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
                            onClick={handleRecoverMissedLeads}
                            disabled={recovering || loading}
                        >
                            {recovering ? 'Recovering...' : 'Recover Missing Leads'}
                        </Button>
                        <Button
                            className={classes.downloadBtn}
                            variant="contained"
                            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <GetAppIcon />}
                            onClick={handleDownloadExcel}
                            disabled={!total || downloading}
                        >
                            {downloading ? 'Downloading...' : 'Download Excel'}
                        </Button>
                    </div>
                </div>

                <Paper className={classes.filtersCard}>
                    <div className={classes.filtersTitle}>
                        <FilterListIcon fontSize="small" />
                        Filters
                    </div>
                    <div className={classes.filtersRow}>
                        <FormControl className={classes.formControl} variant="outlined" size="small">
                            <InputLabel>Time range</InputLabel>
                            <Select
                                value={hours}
                                onChange={(e) => {
                                    setHours(e.target.value);
                                    // Time-range and calendar dates are mutually exclusive filters
                                    setStartDate('');
                                    setEndDate('');
                                }}
                                label="Time range"
                            >
                                {TIME_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="From date"
                            type="date"
                            size="small"
                            variant="outlined"
                            value={startDate}
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                setHours('');
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            label="To date"
                            type="date"
                            size="small"
                            variant="outlined"
                            value={endDate}
                            onChange={(e) => {
                                setEndDate(e.target.value);
                                setHours('');
                            }}
                            InputLabelProps={{ shrink: true }}
                        />

                        <FormControl className={classes.formControl} variant="outlined" size="small">
                            <InputLabel>Source</InputLabel>
                            <Select
                                value={platform}
                                onChange={(e) => setPlatform(e.target.value)}
                                label="Source"
                                renderValue={(selected) => (
                                    <SourceFilterOption
                                        value={selected}
                                        label={SOURCE_OPTIONS.find((opt) => opt.value === selected)?.label || 'All sources'}
                                    />
                                )}
                            >
                                {SOURCE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value || 'all'} value={opt.value}>
                                        <SourceFilterOption value={opt.value} label={opt.label} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl className={classes.formControl} variant="outlined" size="small">
                            <InputLabel>Status</InputLabel>
                            <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                                {STATUS_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value || 'all'} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            className={classes.applyBtn}
                            variant="contained"
                            color="primary"
                            onClick={handleApplyFilters}
                            disabled={loading}
                        >
                            Apply
                        </Button>
                        <Button
                            className={classes.resetBtn}
                            variant="outlined"
                            onClick={handleResetFilters}
                            disabled={loading}
                        >
                            Reset
                        </Button>
                    </div>
                </Paper>

                <div className={classes.statsBar}>
                    <div className={classes.statCard}>
                        <div className={classes.statIcon}><FormatListNumberedIcon fontSize="small" /></div>
                        <div>
                            <div className={classes.statLabel}>Total records</div>
                            <div className={classes.statValue}>{total}</div>
                        </div>
                    </div>
                    {since && (
                        <div className={classes.statCard}>
                            <div className={classes.statIcon}><ScheduleIcon fontSize="small" /></div>
                            <div>
                                <div className={classes.statLabel}>Since</div>
                                <div className={classes.statValue} style={{ fontSize: 14 }}>{formatDateTime(since)}</div>
                            </div>
                        </div>
                    )}
                    <div className={classes.statCard}>
                        <div className={classes.statIcon}><VisibilityOutlinedIcon fontSize="small" /></div>
                        <div>
                            <div className={classes.statLabel}>Showing on this page</div>
                            <div className={classes.statValue}>{leads.length}</div>
                        </div>
                    </div>
                </div>

                <Paper className={classes.tableWrap}>
                    {loading && <LinearProgress />}
                    <div className={classes.tableRelative}>
                    <TableContainer className={classes.tableContainer}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classes.headCell}>Sl No.</TableCell>
                                    <TableCell className={classes.headCell}>Lead No.</TableCell>
                                    <TableCell className={classes.headCell}>Name</TableCell>
                                    <TableCell className={classes.headCell}>Email</TableCell>
                                    <TableCell className={classes.headCell}>Phone</TableCell>
                                    <TableCell className={classes.headCell}>City</TableCell>
                                    <TableCell className={classes.headCell}>Source</TableCell>
                                    <TableCell className={classes.headCell}>Status</TableCell>
                                    <TableCell className={classes.headCell}>Campaign</TableCell>
                                    <TableCell className={classes.headCell}>Form</TableCell>
                                    <TableCell className={classes.headCell}>Form Filled</TableCell>
                                    <TableCell className={classes.headCell}>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {leads.map((lead, index) => (
                                    <TableRow key={lead._id || index} className={classes.tableRow}>
                                        <TableCell className={classes.bodyCell}>
                                            {page * rowsPerPage + index + 1}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            {lead.lead_no && lead.erpLeadId ? (
                                                <Link
                                                    to={`/project-logs/${lead.erpLeadId}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#1C375D', fontWeight: 600, textDecoration: 'underline' }}
                                                >
                                                    {lead.lead_no}
                                                </Link>
                                            ) : (lead.lead_no || 'N/A')}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{lead.fullName || 'N/A'}</TableCell>
                                        <TableCell className={classes.bodyCell}>{lead.email || 'N/A'}</TableCell>
                                        <TableCell className={classes.bodyCell}>{lead.phone || 'N/A'}</TableCell>
                                        <TableCell className={classes.bodyCell}>{lead.city || 'N/A'}</TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            <PlatformSourceBadge platform={lead.platform} />
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{lead.status || 'N/A'}</TableCell>
                                        <TableCell className={classes.bodyCell} style={{ maxWidth: 260 }}>
                                            {lead.campaignName || 'N/A'}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell} style={{ maxWidth: 220 }}>
                                            {lead.formName || 'N/A'}
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{formatDateTime(leadDate(lead))}</TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            <Button
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => {
                                                    setSelectedLead(lead);
                                                    setDetailsOpen(true);
                                                }}
                                            >
                                                View
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!loading && leads.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={12} align="center" style={{ padding: 32 }}>
                                            No leads found for selected filters.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {loading && (
                        <div className={classes.tableLoadingOverlay}>
                            <CircularProgress size={32} />
                            <div className={classes.tableLoadingText}>Loading leads…</div>
                        </div>
                    )}
                    </div>
                    <TablePagination
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        component="div"
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={(_, newPage) => setPage(newPage)}
                        onChangeRowsPerPage={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>

                <LeadDetailsDialog
                    open={detailsOpen}
                    lead={selectedLead}
                    onClose={() => setDetailsOpen(false)}
                    classes={classes}
                />

                <Snackbar
                    open={snack.open}
                    autoHideDuration={6000}
                    onClose={() => setSnack((prev) => ({ ...prev, open: false }))}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <SnackbarContent
                        style={{
                            backgroundColor: snack.severity === 'error' ? '#d32f2f' : '#355025',
                        }}
                        message={snack.message}
                    />
                </Snackbar>
            </div>
        </div>
    );
}

export default MetaLeads;
